import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowUpRight, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money, stockLabel } from "@/lib/site";

export default function ProductCard({ p, index = 0 }) {
  const { addItem, setOpen } = useCart();
  const [askMaster, setAskMaster] = useState(false);
  const [master, setMaster] = useState({ company: "", details: "" });
  const out = p.stock === "out";
  const handleAdd = () => {
    if (p.requires_master_details) { setAskMaster(true); return; }
    addItem(p);
  };
  const confirmAdd = (e) => {
    e.preventDefault();
    if (!master.company.trim()) return;
    addItem(p, 1, {
      master_company: master.company.trim(),
      master_details: master.details.trim(),
      currency: p.currency,
      summary: `Master: ${master.company.trim()}${master.details.trim() ? `\nFLEX details: ${master.details.trim()}` : ""}`,
    });
    setAskMaster(false);
    setMaster({ company: "", details: "" });
    setOpen(true);
  };
  return (
    <div
      data-testid={`product-card-${p.slug}`}
      className="group flex flex-col border border-line bg-card shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300"
    >
      <Link to={`/product/${p.slug}`} data-testid={`product-link-${p.slug}`} className="relative block aspect-[4/3] overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
        {p.badge && (
          <span className={`absolute left-3 top-3 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${p.badge === "MASTER" ? "bg-regal-amber text-void" : "bg-elevated text-neutral-200 border border-linebright"}`}>
            {p.badge}
          </span>
        )}
        <span className={`absolute right-3 top-3 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${out ? "bg-regal-red/90 text-white" : p.stock === "low" ? "bg-elevated text-regal-amber border border-regal-amber/40" : "bg-elevated/90 text-neutral-300 border border-linebright"}`}>
          {stockLabel(p.stock)}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">{p.brand}</p>
        <Link to={`/product/${p.slug}`} data-testid={`product-title-link-${p.slug}`} className="mt-1.5 font-display text-base font-semibold leading-snug hover:text-regal-amber transition-colors">{p.name}</Link>
        <div className="mt-auto flex items-end justify-between pt-5">
          <div>
            {p.price_ex_vat > 0 ? (
              <>
                <p className="font-display text-lg font-bold text-regal-amber" data-testid={`product-price-${p.slug}`}>{money(p.effective_price ?? p.price_ex_vat, p.currency)}</p>
                {p.sale_price > 0 && <p className="font-mono text-[10px] text-neutral-500 line-through">{money(p.price_ex_vat, p.currency)}</p>}
              </>
            ) : (
              <p className="font-display text-lg font-bold text-regal-amber" data-testid={`product-price-${p.slug}`}>POA</p>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={out || p.price_ex_vat === 0}
            data-testid={`product-add-to-cart-${p.slug}`}
            aria-label={`Add ${p.name} to cart`}
            className="grid h-11 w-11 place-items-center border border-linebright text-neutral-200 hover:border-regal-amber hover:bg-regal-amber hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none"
          >
            {p.price_ex_vat === 0 ? <ArrowUpRight size={17} /> : <ShoppingCart size={17} />}
          </button>
        </div>
      </div>
      {askMaster && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 backdrop-blur-sm px-5" data-testid="master-modal" onClick={() => setAskMaster(false)}>
          <form onSubmit={confirmAdd} onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-line bg-white p-7 shadow-[0_24px_64px_rgba(16,24,40,0.25)]">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-display text-base font-bold uppercase tracking-wide text-ink">Master tool details</p>
                <p className="mt-1 text-xs text-neutral-400 leading-relaxed">Required for Slave packages linked to another Master.</p>
              </div>
              <button type="button" onClick={() => setAskMaster(false)} aria-label="Close" data-testid="master-modal-close" className="grid h-9 w-9 shrink-0 place-items-center border border-line text-neutral-400 hover:text-regal-red"><X size={15} /></button>
            </div>
            <div className="space-y-3">
              <input required placeholder="Master company/name *" value={master.company} onChange={(e) => setMaster({ ...master, company: e.target.value })} data-testid="master-modal-company" className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors" />
              <input placeholder="Master FLEX details / serial (if applicable)" value={master.details} onChange={(e) => setMaster({ ...master, details: e.target.value })} data-testid="master-modal-details" className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors" />
            </div>
            <button type="submit" data-testid="master-modal-confirm" className="mt-5 w-full bg-regal-amber py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors">
              Add to Cart
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
